import type {
  AnalyticsChartQueryParams,
  AnalyticsChartResponse,
} from './types';

import { ENDPOINT } from '~/constants';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { USER_ANALYTICS_CHART_NAMES } from './constants';
import {
  zodAnalyticsChartEndpointSchema,
  zodAnalyticsChartQueryParamsSchema,
  zodAnalyticsChartResponseSchema,
} from './validations';

/**
 * Fetches the chart data for analytics from the API with the given query parameters.
 *
 * @param queryParams - The query parameters for fetching the analytics chart.
 * @param options - Additional options for the request (e.g., abort signal).
 * @returns The analytics chart data with labels and datasets.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/User-Activity/operation/get_weekly_active_users_chart_user_activity_chart_weekly_active_users_post
 */
export const fetchAnalyticsChart = async (
  queryParams: AnalyticsChartQueryParams,
  options?: KyRequestOptions,
) => {
  const url = USER_ANALYTICS_CHART_NAMES.includes(queryParams.name)
    ? ENDPOINT.ANALYTICS.GET_USER_CHART(queryParams.name)
    : ENDPOINT.ANALYTICS.GET_DELIVERY_NOTE_CHART(queryParams.name);

  const response = await vestigasApi
    .post(url, {
      json: queryParams,
      validate: {
        body: zodAnalyticsChartQueryParamsSchema.transform(
          ({ name: _name, ...rest }) => ({
            ...rest,
          }),
        ),
        pathSegments: zodAnalyticsChartEndpointSchema,
      },
      ...options,
    })
    .json<AnalyticsChartResponse>();

  const validatedResponse = zodAnalyticsChartResponseSchema.parse(response);

  return validatedResponse;
};
